import React, { useState, useEffect } from 'react';
import { Download, Linkedin, Code, Database, Cloud, Zap, Users, TrendingUp, ExternalLink, Send } from 'lucide-react';

import AnalyticsTracker from './utils/analytics';

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [showBlogPost, setShowBlogPost] = useState(false);
  const [currentArticle, setCurrentArticle] = useState(null);

  // Smooth scrolling and active section detection
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'projects', 'articles', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.analytics = new AnalyticsTracker({
      apiEndpoint: '/api/analytics',
      enableGA4: false // Set to true if you want GA4 integration
    });
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const openArticle = (article) => {
    setCurrentArticle(article);
    setShowBlogPost(true);
  };

  const closeBlogPost = () => {
    setShowBlogPost(false);
    setCurrentArticle(null);
  };

  const projects = [
    {
      title: "Spotlight Content Discovery Infrastructure",
      description: "Architected and led cross-functional transformation of Snapchat's content retrieval system serving 300M+ daily users. Designed streaming pipeline with embedding-based retrieval using HNSW indexing, reducing content freshness from 4 hours to 5 minutes while maintaining sub-100ms p99 latency.",
      tech: ["Go", "HNSW", "Vector Embeddings", "Kafka", "Redis", "ML Infrastructure"],
      impact: "300M+ users, 5min content freshness, 15% engagement lift",
      metrics: "99.9% availability, <100ms p99 latency",
      scope: "Led 8-person cross-functional team across ML, Infrastructure, and Product",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Global Authentication Platform",
      description: "Spearheaded architectural redesign of Snap's phone verification system handling 50M+ daily authentications. Led decomposition of monolithic services into event-driven microservices, implementing circuit breakers, bulkheads, and adaptive rate limiting to achieve 99.99% availability.",
      tech: ["Microservices", "AWS", "DynamoDB", "Event Sourcing", "Circuit Breakers", "Go"],
      impact: "50M+ daily auths, 99.99% availability, $1M+ annual savings",
      metrics: "55% latency reduction (450ms → 200ms p90), 1% conversion increase",
      scope: "Technical leadership of 5 senior engineers across 3 teams",
      icon: <Users className="w-6 h-6" />
    },
    {
      title: "Enterprise SD-WAN Control Plane",
      description: "Technical lead for Cisco's next-generation SD-WAN overlay architecture serving Fortune 500 enterprises. Designed policy engine handling 10M+ routing decisions/sec, implemented zero-touch provisioning, and developed telemetry pipeline processing 1TB+ daily network data.",
      tech: ["Go", "YANG Models", "gRPC", "Protocol Buffers", "BGP", "OSPF"],
      impact: "Fortune 500 deployments, 25% latency improvement, 99.95% uptime",
      metrics: "10M+ routing decisions/sec, 1TB+ daily telemetry",
      scope: "Early technical lead (first 10 engineers) in $1B+ acquisition integration",
      icon: <Database className="w-6 h-6" />
    },
    {
      title: "Cloud-Native Build Infrastructure",
      description: "Designed and implemented multi-architecture build platform supporting 2000+ Snap engineers. Orchestrated migration to ARM64 Graviton instances across AWS and GCP, implemented intelligent workload scheduling, and built cost optimization engine reducing compute spend by 40%.",
      tech: ["Kubernetes", "AWS Graviton", "GCP", "Terraform", "Prometheus", "Grafana"],
      impact: "2000+ engineers, 40% cost reduction, 3x build throughput",
      metrics: "10K+ daily builds, 99.5% success rate, <5min avg build time",
      scope: "Platform team technical leadership, company-wide adoption strategy",
      icon: <Cloud className="w-6 h-6" />
    }
  ];

  const articles = [
    {
      id: 'embedding-retrieval',
      title: "Building Retrieval Systems with Embedding-Based Search",
      description: "Deep dive into implementing ANN indexing with HNSW for real-time content discovery at scale, featuring lessons from Snapchat Spotlight architecture.",
      date: "Coming Soon",
      readTime: "12 min read",
      content: {
        intro: "At Snap, Spotlight serves over 300 million daily users with personalized, engaging content. Behind this experience lies a sophisticated retrieval system that processes billions of content pieces and serves relevant recommendations in under 100ms. This article explores how we transformed our content discovery infrastructure using embedding-based retrieval.",
        sections: [
          {
            title: "The Challenge: From Batch to Real-time",
            content: "Traditional content discovery systems rely on batch processing, updating recommendations every few hours. For a platform like Spotlight where content freshness directly impacts user engagement, 4-hour delays were unacceptable. Users expect to see trending content within minutes of its creation."
          },
          {
            title: "Architecture Overview",
            content: "Our new architecture consists of three core components:\n\n1. **Real-time Embedding Pipeline**: Processes new content as it arrives, generating vector embeddings using transformer models\n2. **HNSW Index Management**: Maintains approximate nearest neighbor indices for sub-100ms retrieval\n3. **Streaming Query Engine**: Handles user queries with real-time personalization"
          },
          {
            title: "Embedding Generation at Scale",
            content: "We process 10M+ new content pieces daily, each requiring embedding generation. Our pipeline uses:\n\n- **Multi-modal transformers** for video, image, and text content\n- **Batch optimization** grouping similar content types\n- **GPU acceleration** with NVIDIA A100s for transformer inference\n- **Async processing** with Kafka for decoupling embedding generation"
          },
          {
            title: "HNSW Implementation Details",
            content: "Hierarchical Navigable Small World (HNSW) graphs provide the best balance of speed and accuracy for our use case:\n\n- **Index sharding** across 100+ machines for horizontal scaling\n- **Dynamic updates** supporting real-time insertions without full rebuilds\n- **Memory optimization** using quantized embeddings (float32 → int8)\n- **Failover mechanisms** with multiple index replicas"
          },
          {
            title: "Performance Optimizations",
            content: "Achieving sub-100ms latency required several optimizations:\n\n- **Connection pooling** reducing TCP handshake overhead\n- **Bloom filters** for negative lookups\n- **L2 cache optimization** with embedding prefetching\n- **Circuit breakers** preventing cascade failures\n- **Adaptive batching** balancing latency vs throughput"
          },
          {
            title: "Results and Impact",
            content: "The new system delivered significant improvements:\n\n- **Content freshness**: 4 hours → 5 minutes\n- **Query latency**: 95th percentile under 100ms\n- **User engagement**: 15% increase in content interaction\n- **System availability**: 99.9% uptime\n- **Cost efficiency**: 30% reduction in compute costs"
          },
          {
            title: "Lessons Learned",
            content: "Key takeaways from this project:\n\n1. **Start with profiling**: Understanding your bottlenecks is crucial before optimization\n2. **Embrace approximate algorithms**: Perfect accuracy isn't always necessary\n3. **Design for failure**: Distributed systems fail; plan for graceful degradation\n4. **Measure everything**: Observability is essential for large-scale systems\n5. **Iterate quickly**: Deploy small changes frequently rather than big bang releases"
          }
        ]
      }
    },
    {
      id: 'microservices-phone-verification',
      title: "Microservices Architecture: Lessons from Phone Verification Redesign",
      description: "How we reduced latency by 55% and saved $1M annually through strategic architectural decisions in Snap's authentication system.",
      date: "Coming Soon",
      readTime: "10 min read"
    },
    {
      id: 'arm64-optimization',
      title: "ARM64 Optimization: The Future of Cloud Computing",
      description: "Cost optimization strategies using ARM64 Graviton instances in Kubernetes workloads, featuring migration patterns and performance analysis.",
      date: "Coming Soon",
      readTime: "8 min read"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white">
      {/* Blog Post Modal */}
      {showBlogPost && currentArticle && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto">
          <div className="min-h-screen py-8 px-4">
            <div className="max-w-4xl mx-auto bg-gray-900/95 backdrop-blur-lg rounded-2xl border border-white/20 overflow-hidden">
              {/* Blog Header */}
              <div className="relative p-8 bg-gradient-to-r from-cyan-900/50 to-purple-900/50">
                <button
                  onClick={closeBlogPost}
                  className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                >
                  ✕
                </button>
                <div className="max-w-3xl">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-cyan-400 font-medium">{currentArticle.date}</span>
                    <span className="text-white/60">•</span>
                    <span className="text-white/60">{currentArticle.readTime}</span>
                  </div>
                  <h1 className="text-4xl font-bold mb-4 leading-tight">
                    {currentArticle.title}
                  </h1>
                  <p className="text-xl text-white/80 leading-relaxed">
                    {currentArticle.description}
                  </p>
                </div>
              </div>

              {/* Blog Content */}
              <div className="p-8">
                <div className="prose prose-invert max-w-none">
                  {/* Introduction */}
                  <p className="text-lg text-white/90 leading-relaxed mb-8 border-l-4 border-cyan-400 pl-6 italic">
                    {currentArticle.content.intro}
                  </p>

                  {/* Article Sections */}
                  {currentArticle.content.sections.map((section, index) => (
                    <div key={index} className="mb-8">
                      <h2 className="text-2xl font-bold mb-4 text-cyan-400">
                        {section.title}
                      </h2>
                      <div className="text-white/80 leading-relaxed space-y-4">
                        {section.content.split('\n\n').map((paragraph, pIndex) => {
                          // Handle bullet points
                          if (paragraph.includes('- **')) {
                            const lines = paragraph.split('\n');
                            return (
                              <div key={pIndex} className="space-y-3">
                                {lines.map((line, lIndex) => {
                                  if (line.startsWith('- **')) {
                                    const [, boldText, regularText] = line.match(/- \*\*(.*?)\*\*:?\s*(.*)/);
                                    return (
                                      <div key={lIndex} className="flex items-start gap-3">
                                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                                        <p>
                                          <strong className="text-purple-400">{boldText}</strong>
                                          {regularText && `: ${regularText}`}
                                        </p>
                                      </div>
                                    );
                                  } else if (line.trim()) {
                                    return <p key={lIndex}>{line}</p>;
                                  }
                                  return null;
                                })}
                              </div>
                            );
                          } else if (paragraph.match(/^\d+\./)) {
                            // Handle numbered lists
                            const items = paragraph.split('\n').filter(line => line.trim());
                            return (
                              <div key={pIndex} className="space-y-3">
                                {items.map((item, iIndex) => {
                                  const [, number, boldText, regularText] = item.match(/(\d+)\.\s*\*\*(.*?)\*\*:?\s*(.*)/) || [null, iIndex + 1, '', item];
                                  return (
                                    <div key={iIndex} className="flex items-start gap-3">
                                      <span className="text-cyan-400 font-bold text-sm bg-cyan-400/20 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        {number}
                                      </span>
                                      <p>
                                        {boldText && <strong className="text-cyan-400">{boldText}</strong>}
                                        {regularText && (boldText ? `: ${regularText}` : regularText)}
                                      </p>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          } else {
                            // Regular paragraph
                            return (
                              <p key={pIndex} className="leading-relaxed">
                                {paragraph}
                              </p>
                            );
                          }
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Author Bio */}
                  <div className="mt-12 p-6 bg-white/10 rounded-xl border border-white/20">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-xl font-bold">AM</span>
                      </div>
                      <div>
                        <h3 className="font-semibold">Ajay Mishra</h3>
                        <p className="text-white/60 text-sm">
                          Staff Software Engineer at Snap Inc, building distributed systems at scale
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/20 backdrop-blur-lg z-50 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Ajay Mishra
            </div>
            <div className="hidden md:flex space-x-8">
              {['About', 'Projects', 'Articles', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`transition-colors hover:text-cyan-400 ${
                    activeSection === item.toLowerCase() ? 'text-cyan-400' : 'text-white/80'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 animate-pulse"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-fade-in">
            Distributed Systems Engineer
          </h1>
          <p className="text-xl md:text-2xl text-white/80 mb-8 leading-relaxed">
            Building scalable infrastructure at <span className="text-yellow-400 font-semibold">Snap Inc</span>
            <br />
            Specializing in retrieval systems, microservices, and performance optimization
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => scrollToSection('projects')}
              className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full font-semibold hover:scale-105 transition-transform duration-200"
            >
              View My Work
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="px-8 py-3 border border-white/30 rounded-full font-semibold hover:bg-white/10 transition-colors duration-200"
            >
              Reach Out for Mentorship
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 relative">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            About Me
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-white/80 mb-6 leading-relaxed">
                Senior Software Engineer at Snap Inc with 10+ years of experience building distributed systems at scale. 
                Currently leading retrieval infrastructure improvements that serve millions of users daily.
              </p>
              <p className="text-lg text-white/80 mb-6 leading-relaxed">
                Previously at Cisco Systems, I was an early engineer in the Viptela-Cisco SD-WAN integration, 
                driving seamless deployment of software-defined networking solutions.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Code className="w-4 h-4 text-cyan-400" />
                  <span>Go, Java, Python</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Cloud className="w-4 h-4 text-purple-400" />
                  <span>AWS, GCP, Kubernetes</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Database className="w-4 h-4 text-pink-400" />
                  <span>DynamoDB, Spanner, Redis</span>
                </div>
              </div>
              <a
                href="/Ajay_Resume_Snap.pdf"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full font-semibold hover:scale-105 transition-transform duration-200"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="w-4 h-4" />
                Download Resume
              </a>
            </div>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-semibold mb-2 text-cyan-400">Current Focus</h3>
                <p className="text-white/80">Leading retrieval infrastructure at Snap, improving content discovery through ML/AI and real-time systems.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-semibold mb-2 text-purple-400">Patents & Innovation</h3>
                <p className="text-white/80">3 US Patents in networking and SD-WAN technologies, with focus on performance optimization.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-semibold mb-2 text-pink-400">Mentorship</h3>
                <p className="text-white/80">Passionate about helping engineers grow in distributed systems and backend architecture.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 bg-black/20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            Featured Projects
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-lg">
                    {project.icon}
                  </div>
                  <h3 className="text-xl font-semibold">{project.title}</h3>
                </div>
                <p className="text-white/80 mb-4 leading-relaxed">{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tech.map((tech, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-green-400 font-semibold">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-sm">{project.impact}</span>
                  </div>
                  <div className="flex items-center gap-2 text-cyan-400 font-medium">
                    <span className="text-xs px-2 py-1 bg-cyan-400/20 rounded">METRICS</span>
                    <span className="text-sm">{project.metrics}</span>
                  </div>
                  <div className="flex items-center gap-2 text-purple-400 font-medium">
                    <span className="text-xs px-2 py-1 bg-purple-400/20 rounded">SCOPE</span>
                    <span className="text-sm">{project.scope}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section id="articles" className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            Recent Articles
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105 cursor-pointer"
                onClick={() => article.content ? openArticle(article) : null}
              >
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm text-cyan-400 font-medium">{article.date}</span>
                  <span className="text-sm text-white/60">{article.readTime}</span>
                </div>
                <h3 className="text-lg font-semibold mb-3 leading-tight">{article.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed mb-4">{article.description}</p>
                <div className="flex items-center gap-2 text-purple-400 font-medium text-sm">
                  <span>{article.content ? 'Read Article' : 'Coming Soon'}</span>
                  {article.content && <ExternalLink className="w-3 h-3" />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20">
        <h2 className="text-5xl font-bold text-center mb-16 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
          Let's Connect
        </h2>
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16 items-start px-6">
          {/* Left: Mentorship info and LinkedIn */}
          <div>
            <h3 className="text-3xl font-bold mb-4 text-white">Reach Out for Mentorship</h3>
            <p className="text-white/80 mb-8 text-lg">
              I'm passionate about helping engineers grow in distributed systems, backend architecture, and career development. Whether you're looking for technical guidance or career advice, I'd love to help.
            </p>
            <div className="flex items-center gap-4 mb-4">
              <a
                href="https://linkedin.com/in/ajaymishrancsu"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors text-lg"
                aria-label="LinkedIn Profile"
              >
                <Linkedin className="w-6 h-6" />
                linkedin.com/in/ajaymishrancsu
              </a>
            </div>
          </div>
          {/* Right: Contact form */}
          <form 
            name="contact" 
            method="POST" 
            data-netlify="true" 
            data-netlify-honeypot="bot-field"
            className="space-y-6"
          >
            <input type="hidden" name="form-name" value="contact" />
            <input type="hidden" name="bot-field" />
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <input
                type="text"
                name="name"
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-cyan-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                name="email"
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-cyan-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Message</label>
              <textarea
                name="message"
                required
                rows="4"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-cyan-400 transition-colors resize-none"
              ></textarea>
            </div>
            <button
              type="submit"
              className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-lg font-semibold hover:scale-105 transition-transform duration-200 flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send Message
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-white/60">
            © 2025 Ajay Mishra. Built with React and passion for distributed systems.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;